import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';

const AdminAuthContext = createContext(null);

const ADMIN_ROLE = 'admin';

async function fetchAdminProfile(userId) {
  if (!userId) {
    return { data: null, error: new Error('Missing user id') };
  }

  const { data, error } = await supabase
    .from('admin_profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  return { data, error };
}

export function AdminAuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const resetAuthState = useCallback(() => {
    setSession(null);
    setProfile(null);
    setError(null);
  }, []);

  const loadSession = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        throw sessionError;
      }

      const nextSession = sessionData?.session ?? null;
      setSession(nextSession);

      if (!nextSession?.user?.id) {
        setProfile(null);
        return;
      }

      const { data: adminProfile, error: profileError } = await fetchAdminProfile(nextSession.user.id);
      if (profileError) {
        throw profileError;
      }

      setProfile(adminProfile ?? null);
    } catch (err) {
      console.error('Failed to load admin session', err);
      setError(err instanceof Error ? err : new Error('Unknown auth error'));
      resetAuthState();
    } finally {
      setIsLoading(false);
    }
  }, [resetAuthState]);

  useEffect(() => {
    loadSession();

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, authSession) => {
      setSession(authSession ?? null);

      if (authSession?.user?.id) {
        fetchAdminProfile(authSession.user.id)
          .then(({ data: adminProfile, error: profileError }) => {
            if (profileError) {
              console.error('Failed to fetch admin profile after auth change', profileError);
              setProfile(null);
              return;
            }
            setProfile(adminProfile ?? null);
          })
          .catch((profileErr) => {
            console.error('Unexpected admin profile load error', profileErr);
            setProfile(null);
          });
      } else {
        setProfile(null);
      }
    });

    return () => {
      subscription?.subscription?.unsubscribe();
    };
  }, [loadSession]);

  const signIn = useCallback(async (email, password) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        throw signInError;
      }

      const nextSession = data?.session ?? null;
      setSession(nextSession);

      if (!nextSession?.user?.id) {
        throw new Error('Missing user information for admin session');
      }

      const { data: adminProfile, error: profileError } = await fetchAdminProfile(nextSession.user.id);
      if (profileError) {
        throw profileError;
      }

      if (!adminProfile || adminProfile.role !== ADMIN_ROLE) {
        throw new Error('You do not have admin access. Contact support.');
      }

      setProfile(adminProfile);
      return { data: adminProfile, error: null };
    } catch (err) {
      console.error('Admin sign-in failed', err);
      setError(err instanceof Error ? err : new Error('Unknown sign-in error'));
      resetAuthState();
      return { data: null, error: err };
    } finally {
      setIsLoading(false);
    }
  }, [resetAuthState]);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut({ scope: 'global' });
    } catch (err) {
      console.error('Failed to sign out admin', err);
    } finally {
      resetAuthState();
    }
  }, [resetAuthState]);

  const registerAdmin = useCallback(async ({ fullName, email }) => {
    // Always pull the latest authenticated user to avoid stale state during email verification flows
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user?.id) {
      throw new Error('Must be signed in to register as admin');
    }

    const userId = userData.user.id;

    const payload = {
      user_id: userId,
      full_name: fullName,
      email,
      role: ADMIN_ROLE,
      status: 'active',
    };

    const { data, error: upsertError } = await supabase
      .from('admin_profiles')
      .upsert(payload, { onConflict: 'user_id' })
      .select()
      .maybeSingle();

    if (upsertError) {
      throw upsertError;
    }

    setProfile(data ?? payload);
    return data ?? payload;
  }, []);

  const value = useMemo(() => ({
    session,
    profile,
    isLoading,
    error,
    signIn,
    signOut,
    reload: loadSession,
    registerAdmin,
    hasAdminAccess: Boolean(profile?.role === ADMIN_ROLE),
  }), [session, profile, isLoading, error, signIn, signOut, loadSession, registerAdmin]);

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}
